import dayjs, { Dayjs } from "utils/dayjs-config";

interface IDateUtils {
  dateTimeObj(
    date?: TDateTimeObj | string | Date | number | null,
    format?: string
  ): Dayjs;
  format(date: any, format?: string | { in?: string; out?: string }): string;
  formatUtc(date: any, format?: string): string;
  formatDateWithLunchTime(date: any, inFormat?: string): string;
}

export const ISO_DATE_FORMAT_1 = "YYYY-MM-DDTHH:mm:ssZZ";
export const DATE_FORMAT_MMDDYYYY = "MM-DD-YYYY";
export const DATE_FORMAT_YYYYMMDD = "YYYY-MM-DD";
export const DATE_FORMAT_MMDDYYYY_HHMM_A = "MM-DD-YYYY hh:mm A";
export const DATE_FORMAT_MMDDYYYY_AT_HHMM_A = "MM-DD-YYYY [at] hh:mmA";

export const DATE_FORMAT_MMDDYYYY_HHMM_A_Z = "MM-DD-YYYY hh:mm A (z)";
export const DATE_FORMAT_MMDDYYYY_HHMM_Z = "DD-MMM-YYYY HH:mm:ss Z";
export const INPUT_DATE_FORMAT = "YYYY-MM-DDTHH:mm:ss.SSSZ";

export type TDateTimeObj = Dayjs;
export type AmPmType = "AM" | "PM" | "am" | "pm";

// Returns true only when the input string is an unambiguous instant — either
// a Z suffix or an explicit ±HH:MM offset. Naive datetimes ("2024-11-20T00:00:00"
// with no Z/offset) and bare calendar dates ("2026-12-25") are zone-independent
// and must NOT be shifted to the project zone — doing so flips the displayed
// day when the user's machine zone differs from the project zone (e.g. machine
// IST vs project EDT, a 9.5-hour gap that rolls dates back by one).
//
// Backend convention here matches moment-timezone's: naive ISO = local calendar
// time, Z/offset-tagged = absolute instant.
const isInstantString = (input: string): boolean =>
  /[zZ]$/.test(input) || /[+-]\d{2}:?\d{2}$/.test(input);

// Always parse via customParseFormat — never rely on native Date.parse.
// Safari rejects common non-ISO forms (MM-DD-YYYY, spaced datetimes) that Chrome
// accepts, which previously scrambled AG Grid date column sorting on Mac.
// YYYYMMDD is the shape getNormalizedDate emits (TemporalLayout map keys).
export const DATE_PARSE_FORMATS = [
  DATE_FORMAT_MMDDYYYY,
  "MM/DD/YYYY",
  DATE_FORMAT_YYYYMMDD,
  "YYYYMMDD",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm:ssZZ",
  "YYYY-MM-DD HH:mm",
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD HH:mm:ss.SSS",
  DATE_FORMAT_MMDDYYYY_HHMM_A,
  DATE_FORMAT_MMDDYYYY_HHMM_A_Z
] as const;

const parseFormatsFor = (preferred?: string): string[] => {
  if (!preferred) return [...DATE_PARSE_FORMATS];
  return [
    preferred,
    ...DATE_PARSE_FORMATS.filter((format) => format !== preferred)
  ];
};

const parseCalendarDateString = (date: string, preferredFormat?: string) =>
  dayjs(date, parseFormatsFor(preferredFormat));

type SortableDateValue = string | Date | number | Dayjs | null;

const parseSortableDate = (
  value: SortableDateValue,
  preferredFormat?: string
): Dayjs | null => {
  if (value == null || value === "") return null;

  let parsed: Dayjs;
  if (typeof value === "string") {
    parsed = isInstantString(value)
      ? dayjs(value).tz()
      : parseCalendarDateString(value, preferredFormat);
  } else {
    parsed = dayjs(value).tz();
  }

  return parsed.isValid() ? parsed : null;
};

const compareDateValues = (
  value1: SortableDateValue,
  value2: SortableDateValue,
  preferredFormat: string | undefined,
  isInverted?: boolean
): number => {
  const emptySortSign = isInverted ? -1 : 1;
  const date1 = parseSortableDate(value1, preferredFormat);
  const date2 = parseSortableDate(value2, preferredFormat);

  // Empties / unparseable values always sort to the bottom (asc and desc).
  if (!date1 && !date2) return 0;
  if (date1 && !date2) return -1 * emptySortSign;
  if (!date1 && date2) return 1 * emptySortSign;
  if (!date1 || !date2) return 0;

  const t1 = date1.startOf("day").valueOf();
  const t2 = date2.startOf("day").valueOf();
  if (t1 === t2) return 0;
  return t1 > t2 ? 1 : -1;
};

const compareDateTimeValues = (
  value1: SortableDateValue,
  value2: SortableDateValue,
  preferredFormat: string | undefined,
  isInverted?: boolean
): number => {
  const emptySortSign = isInverted ? -1 : 1;
  const date1 = parseSortableDate(value1, preferredFormat);
  const date2 = parseSortableDate(value2, preferredFormat);

  if (!date1 && !date2) return 0;
  if (date1 && !date2) return -1 * emptySortSign;
  if (!date1 && date2) return 1 * emptySortSign;
  if (!date1 || !date2) return 0;

  const t1 = date1.valueOf();
  const t2 = date2.valueOf();
  if (t1 === t2) return 0;
  return t1 > t2 ? 1 : -1;
};

export const DateUtils: IDateUtils = {
  // For "now" and full timestamps, .tz() picks up dayjs.tz.setDefault(projectZone)
  // set by ProjectProvider — mirrors moment-timezone's setDefault that hijacked
  // plain moment(). For date-only strings we deliberately stay in machine-local
  // so the calendar date displayed equals the calendar date the API returned.
  dateTimeObj(date?: TDateTimeObj | string | Date | number, format?: string) {
    if (!date) return dayjs.tz();
    if (dayjs.isDayjs(date) || date instanceof Date) return dayjs(date).tz();
    if (typeof date === "string") {
      // Instant strings (Z or ±HH:MM) need the native ISO 8601 parser — the
      // customParseFormat array below carries no token that matches a literal
      // Z, so dayjs would fall back to a partial "YYYY-MM-DD" match and silently
      // drop the time, causing a later .tz() to roll the calendar day back.
      if (isInstantString(date)) return dayjs(date).tz();
      // Prefer the caller format, then fall back so grid sort still works when
      // AG Grid passes raw ISO while the comparator requested MM-DD-YYYY (or
      // the reverse after valueGetter formatting).
      return parseCalendarDateString(date, format);
    }
    return dayjs(date).tz();
  },

  format(date: any, format?: string | { in?: string; out?: string }) {
    if (!date) return "";
    if (format && typeof format === "object") {
      // {in, out} is used for calendar-date reformatting (e.g. YYYY-MM-DD → MM-DD-YYYY).
      // Skip the project-zone shift so the calendar date stays stable across zones.
      const parsed = format.in
        ? dayjs(date, format.in)
        : parseCalendarDateString(String(date));
      return parsed.isValid()
        ? parsed.format(format.out || DATE_FORMAT_MMDDYYYY)
        : "";
    }

    const out = format || DATE_FORMAT_MMDDYYYY;
    if (typeof date === "string" && !isInstantString(date)) {
      const parsed = parseCalendarDateString(date);
      return parsed.isValid() ? parsed.format(out) : "";
    }
    const parsed = dayjs(date).tz();
    return parsed.isValid() ? parsed.format(out) : "";
  },

  // Like format, but stays on the UTC clock — no project-zone shift. Use for
  // audit / activity timestamps that the API stores and returns as UTC.
  formatUtc(date: any, format?: string) {
    if (!date) return "";
    const out = format || DATE_FORMAT_MMDDYYYY;
    const parsed = dayjs.utc(date);
    return parsed.isValid() ? parsed.format(out) : "";
  },

  // `!date` only screens out null/undefined. An *invalid* dayjs — what
  // `dayjs(null)` returns, and what a cleared DatePicker produces once its null
  // is passed through dayjs() — is an object, so it is truthy and sails past
  // that guard. Formatting it yielded the literal "Invalid DateT13:23:24+00:00",
  // which then got stored in state and submitted as an `actual_date`. Validate
  // the parsed result, matching how `format` above already handles junk.
  formatDateWithLunchTime(date: any, inFormat?: string) {
    if (!date) return "";
    try {
      const parsed = dayjs(
        date,
        inFormat || [DATE_FORMAT_MMDDYYYY, DATE_FORMAT_YYYYMMDD]
      );
      if (!parsed.isValid()) return "";
      return `${parsed.format(DATE_FORMAT_YYYYMMDD)}T13:23:24+00:00`;
    } catch {
      return "";
    }
  }
};

export const DateFilter = {
  comparator: (
    value1: SortableDateValue,
    value2: SortableDateValue,
    _nodeA?: any,
    _nodeB?: any,
    isInverted?: boolean
  ): number =>
    compareDateValues(value1, value2, DATE_FORMAT_MMDDYYYY, isInverted),

  customComparator: (
    value1: SortableDateValue,
    value2: SortableDateValue,
    format: string = DATE_FORMAT_MMDDYYYY,
    _nodeA?: any,
    _nodeB?: any,
    isInverted?: boolean
  ): number => compareDateValues(value1, value2, format, isInverted),

  comparatorWithoutFormat: (
    value1: SortableDateValue,
    value2: SortableDateValue,
    _nodeA?: any,
    _nodeB?: any,
    isInverted?: boolean
  ): number => compareDateValues(value1, value2, undefined, isInverted),

  dateTimeComparator: (
    value1: SortableDateValue,
    value2: SortableDateValue,
    _nodeA?: any,
    _nodeB?: any,
    isInverted?: boolean
  ): number =>
    compareDateTimeValues(
      value1,
      value2,
      DATE_FORMAT_MMDDYYYY_HHMM_A_Z,
      isInverted
    )
};

export const getCurrentTimeZoneInfo = () => {
  // Honor dayjs.tz.setDefault(projectZone) set by ProjectProvider.
  // dayjs.tz.guess() always returned the OS zone, masking the project zone.
  const dt = dayjs.tz();
  return {
    code: dt.format("z")
  };
};

export const convert24HourTo12Hour = (hourIn24Format: number) => {
  if (hourIn24Format === 0) {
    return { hour: 12, minute: 0, amPm: "AM" };
  }

  if (hourIn24Format > 0 && hourIn24Format < 12) {
    return { hour: hourIn24Format, minute: 0, amPm: "AM" };
  }

  if (hourIn24Format === 12) {
    return { hour: 12, minute: 0, amPm: "PM" };
  }

  if (hourIn24Format > 12 && hourIn24Format < 24) {
    return { hour: hourIn24Format - 12, minute: 0, amPm: "PM" };
  }

  return { hour: 0, minute: 0, amPm: "AM" };
};

export const convert12HourTo24Hour = (
  hourIn12Format: number,
  amPm: AmPmType
) => {
  if (hourIn12Format === 12 && amPm === "AM") {
    return 0;
  }

  if (hourIn12Format === 12 && amPm === "PM") {
    return 12;
  }

  if (hourIn12Format > 0 && hourIn12Format < 12 && amPm === "AM") {
    return hourIn12Format;
  }

  if (hourIn12Format > 0 && hourIn12Format < 12 && amPm === "PM") {
    return hourIn12Format + 12;
  }

  return 0;
};

export const getHoursAndMinutesInVariousFormats = () => {
  const hoursIn24HourFormat = Array.from({ length: 24 }, (_, i) => i);
  const hoursIn12HourFormat = Array.from({ length: 13 }, (_, i) => i).filter(
    (hour) => hour !== 0
  );
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  // option object with label and value
  const hoursIn12HourFormatOptions = hoursIn12HourFormat.map((hour) => ({
    label: hour,
    value: hour
  }));

  const hoursIn24HourFormatOptions = hoursIn24HourFormat.map((hour) => ({
    label: hour,
    value: hour
  }));

  const minutesOptions = minutes.map((minute) => ({
    label: minute,
    value: minute
  }));

  return {
    options: {
      hoursIn12HourFormatOptions,
      hoursIn24HourFormatOptions,
      minutesOptions
    },
    values: {
      hoursIn12HourFormat,
      hoursIn24HourFormat,
      minutes
    }
  };
};
